/**
 * Country-facts data layer (uniqueness block, 2026-07-13 audit item 1.3).
 *
 * Loads data/country-facts.json (193 wikidata-sourced entries: capital,
 * currency, languages, population, area) as its own lazy Vite chunk —
 * same import.meta.glob pattern as data/articles.ts, so the ~60 KB gzip
 * payload never lands in the boot-critical index chunk.
 *
 * Loaded alongside bootArticles() in main.ts so route render() can read
 * it synchronously. Mirrors backend util/country-body.ts loadFacts() —
 * both sides render the same block (Googlebot renders the SPA; non-JS
 * crawlers get the SSR body — the two must not diverge).
 */

export interface CountryFacts {
  capital?: string;
  currency?: string;
  languages?: string[];
  population?: number;
  area_km2?: number;
}

let FACTS: Record<string, CountryFacts> = {};

const loader = import.meta.glob<{ default: Record<string, CountryFacts> }>(
  '../../../data/country-facts.json',
);

export async function ensureFactsLoaded(): Promise<void> {
  if (Object.keys(FACTS).length > 0) return;
  const entries = Object.values(loader);
  if (entries.length === 0) return;
  const mod = await entries[0]!();
  FACTS = mod.default;
}

/** Sync read — returns {} until ensureFactsLoaded() resolves. */
/** For RouteDef.whenReady: null when the facts are in, else the load to wait for. */
export function factsPending(): Promise<void> | null {
  return Object.keys(FACTS).length > 0 ? null : ensureFactsLoaded();
}

export function getCountryFacts(slug: string): CountryFacts {
  return FACTS[slug] ?? {};
}
